/**
 * StatusPill — shared aspect. Rounded pill chip with optional left
 * dot indicator and uppercase label. Three tones:
 *   sage  — sage chip on light surface (active / passed states)
 *   amber — amber chip (in-review / pending states)
 *   grey  — neutral chip on dark surface (closed / muted states)
 *
 * Used by WorkspaceHero's status chip, ProgramCard's status chip,
 * StatusCard's tone chip, etc. Reusable for any small status indicator.
 *
 * brand-aspect: consumed by Student portal (TeamBrowseRow "Locked"/"Full"
 * chips on the competition Team tab).
 */
import { Text, View } from "react-native";

import { COLORS, RADII, SPACING, STROKES } from "../../lib/brand";
import type { PaneTheme } from "../brand/paneThemes";

export type StatusPillTone = "sage" | "amber" | "grey";

export type StatusPillProps = {
  theme: PaneTheme;
  label: string;
  tone: StatusPillTone;
  /** Default true. Set false for a label-only chip. */
  showDot?: boolean;
};

export function StatusPill({
  theme,
  label,
  tone,
  showDot = true,
}: StatusPillProps) {
  const cfg = (() => {
    if (tone === "sage") return {
      color: COLORS.SAGE.hex,
      bg: theme.successTintSoft,
      border: theme.successBorderSoft,
    };
    if (tone === "amber") return {
      color: theme.accent,
      bg: theme.accentTintSoft,
      border: theme.accentBorderHover,
    };
    return {
      color: theme.textOnSidebarDim,
      bg: theme.surfaceDividerFaint,
      border: theme.surfaceDividerStrong,
    };
  })();
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingHorizontal: 11,
        paddingVertical: SPACING.xxs,
        borderRadius: RADII.full,
        borderWidth: STROKES.hairline,
        backgroundColor: cfg.bg,
        borderColor: cfg.border,
      }}
    >
      {showDot ? (
        <View
          style={{
            width: 6,
            height: 6,
            borderRadius: RADII.full,
            backgroundColor: cfg.color,
          }}
        />
      ) : null}
      <Text
        style={{
          fontFamily: theme.bodyBoldFont,
          fontSize: 9.5,
          letterSpacing: 1.5,
          color: cfg.color,
          textTransform: "uppercase",
        }}
      >
        {label}
      </Text>
    </View>
  );
}
